import { Component } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, EMPTY, finalize, tap } from 'rxjs';
import { VALID_PASSWORD } from '../../../../../core/constants/general.constant';
import { SuccessPopupComponent } from '../../../../../core/libraries/popup/components/success-popup/success-popup.component';
import { ISignupData } from '../../../../../core/models/auth.model';
import { AuthService } from '../../../../../core/services/api/auth/auth.service';
import { LoaderService } from '../../../../../core/services/loader/loader.service';

@Component({
    selector: 'app-signup',
    standalone: false,
    templateUrl: './signup.component.html',
    styleUrl: './signup.component.scss',
})
export class SignupComponent {
    protected email = this.route.snapshot.queryParamMap.get('email') || '';
    protected signupForm = this.fb.group({
        fullName: ['', [Validators.required]],
        password: ['', [Validators.required, Validators.minLength(8), Validators.pattern(VALID_PASSWORD)]],
    });
    protected showPassword = false;

    constructor(
        private authService: AuthService,
        private fb: NonNullableFormBuilder,
        private loaderService: LoaderService,
        private route: ActivatedRoute,
        private router: Router,
        private dialog: MatDialog,
    ) {}

    get password(): string {
        return this.signupForm.get('password')?.value || '';
    }

    protected hasMinLength(): boolean {
        return this.password.length >= 8;
    }

    protected hasNumber(): boolean {
        return /\d/.test(this.password);
    }

    protected hasLetter(): boolean {
        return /(?=.*[a-z])(?=.*[A-Z])/.test(this.password);
    }

    protected submit(): void {
        if (!this.signupForm.valid || !this.email) {
            return;
        }

        const body: ISignupData = {
            email: this.email,
            password: this.signupForm.controls.password.value,
            name: this.signupForm.controls.fullName.value,
        };

        this.loaderService.show();
        this.authService
            .signup(body)
            .pipe(
                tap(() => {
                    this.router.navigate(['/verify-email'], {
                        queryParams: { email: this.email },
                    });
                }),
                catchError((error) => {
                    const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                    errorPopupConfig.data = {
                        title: `Error`,
                        message: error.error?.message,
                    };
                    this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                    console.error('Error on signup', error);
                    return EMPTY;
                }),
                finalize(() => {
                    this.loaderService.hide();
                }),
            )
            .subscribe();
    }

    protected togglePasswordVisibility(): void {
        this.showPassword = !this.showPassword;
    }
}
